import { ReactNode } from 'react'
import cx from 'classnames'
import { GlassSurface, Stack, Text } from '~m/designSystem'
import styles from './StatPill.module.css'

export type StatPillVariant = 'default' | 'flag' | 'alert'

export interface StatPillProps {
  /** The headline number or value shown large */
  value: ReactNode
  /** The caption describing the value */
  label: ReactNode
  /** Optional icon shown before the label */
  icon?: ReactNode
  /**
   * Semantic emphasis. `flag` tints the pill for values that
   * need attention, `alert` for values that are blocking.
   */
  variant?: StatPillVariant
  /** Additional CSS class */
  className?: string
}

/**
 * A compact summary metric used in the sync summary row — a
 * prominent value above a captioned label. Built on GlassSurface
 * so it shares the app's panel styling.
 */
export function StatPill({
  value,
  label,
  icon,
  variant = 'default',
  className,
}: StatPillProps) {
  return (
    <GlassSurface
      variant="panel"
      className={cx(styles.StatPill, className, {
        [styles.isFlag]: variant === 'flag',
        [styles.isAlert]: variant === 'alert',
      })}
    >
      <Text
        variant="section-title"
        component="span"
        className={styles.StatPill__value}
      >
        {value}
      </Text>
      <Stack
        component="span"
        direction="row"
        alignItems="center"
        gap={1}
      >
        {icon}
        <Text variant="field-label" component="span">
          {label}
        </Text>
      </Stack>
    </GlassSurface>
  )
}
